<template>
  <div>
    <v-row no-gutters>
      <v-col cols="12" class="mb-6 text-h6"
        >Alerts
        <span class="mx-4">
          <v-btn @click="showAddNewAlertDialog" depressed x-small color="backgroundLight" class="py-3">
            <v-icon color="primary"> mdi-plus </v-icon>
          </v-btn></span
        ></v-col
      >
      <v-col cols="12"
        ><v-text-field
          dense
          class="text-caption"
          clearable
          hide-details
          placeholder="Search here..."
          v-model="search_text"
          outlined
        ></v-text-field>
      </v-col>
      <v-col cols="12" v-if="current_device_id">
        <v-chip
          class="mt-4"
          small
          :color="`${filter_current_device_alerts ? 'success' : 'background'}`"
          :text-color="`${filter_current_device_alerts ? 'backgroundLight' : 'backgroundDark'}`"
          :close="filter_current_device_alerts"
          @click:close="updateDeviceFilter(false)"
          @click="updateDeviceFilter(true)"
        >
          This Device
        </v-chip>
      </v-col>
      <v-col cols="12" class="mt-6">
        <v-tabs slider-size="3" v-model="current_tab" centered>
          <v-tab class="text-caption">Rules</v-tab>
          <v-tab class="text-caption"
            >Logs
            <v-avatar v-if="unseen_alerts" color="primary" size="20" class="mb-4">
              <span class="white--text text-caption">{{ unseen_alerts }}</span>
            </v-avatar>
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="current_tab">
          <v-tab-item>
            <v-list v-if="filtered_alerts && filtered_alerts.length > 0">
              <v-list-item
                class="mb-2 border-bottom alert"
                v-for="alert in filtered_alerts"
                :key="`alert_${alert.alert_id}`"
              >
                <v-list-item-content>
                  <v-list-item-title class="primaryOutline--text text-subtitle-2">
                    <v-row no-gutters class="text-caption">
                      <v-col cols="10">
                        <v-row no-gutters>
                          <v-col cols="9" class="text-truncate">
                            <router-link class="pointer" :to="`/scavenger/${alert.device_id}`"
                              >{{ alert.alert_name }}
                            </router-link>
                          </v-col>
                          <v-col cols="3" class="text-right text-capitalize">
                            <v-chip
                              x-small
                              label
                              outlined
                              :color="alert.alert_type.includes('alarm') ? 'alarmText' : 'cautionText'"
                              >{{ alert.alert_type }}</v-chip
                            ></v-col
                          >
                        </v-row>
                      </v-col>
                      <v-col cols="2" class="text-right"
                        ><v-btn x-small icon color="primary" @click="showEditAlertDialog(alert)">
                          <v-icon x-small> mdi-pencil </v-icon> </v-btn
                        ><v-btn x-small icon color="secondary" @click="deleteAlertRule(alert.alert_id)">
                          <v-icon x-small> mdi-delete </v-icon>
                        </v-btn></v-col
                      ></v-row
                    >
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <v-row no-gutters class="text-caption">
                      <v-col cols="7">
                        <v-row no-gutters class="text-no-wrap">
                          <v-col
                            cols="auto"
                            :style="{ maxWidth: '75%' }"
                            :title="$config.ScvDeviceFields[alert.field] || alert.field"
                            class="text-truncate"
                          >
                            {{ $config.ScvDeviceFields[alert.field] || alert.field }}
                          </v-col>
                          <v-col cols="3">{{ alert.trigger_operator }} {{ alert.trigger_value }}</v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="3"> {{ alert.expiration_date }} </v-col>
                      <v-col cols="2" :class="`${alert.expired ? 'error' : 'success'}--text`"
                        ><b>{{ alert.expired ? "Expired" : "Active" }}</b></v-col
                      >
                    </v-row>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-row v-else class="d-flex justify-center mt-3">
              <v-col cols="12" class="text-caption text-center font-weight-bold primaryGrey--text"
                >No rules found!</v-col
              ></v-row
            >
          </v-tab-item>
          <v-tab-item>
            <div v-if="unseen_alerts" class="mt-6 d-flex justify-end">
              <v-btn
                x-small
                class="ma-1 font-weight-bold text-decoration-underline"
                color="info"
                plain
                @click="markAllRead"
              >
                Mark All Read
              </v-btn>
            </div>
            <v-list v-if="active_alerts && active_alerts.length > 0">
              <v-list-item
                v-for="(alert, index) in active_alerts"
                :key="`alert_${alert.alert_id}_${index}`"
                class="border-bottom alert"
              >
                <v-list-item-content :class="`px-2 ${!alert.is_seen ? 'backgroundLight' : ''}`">
                  <v-list-item-title class="primaryOutline--text text-subtitle-2 text-wrap">
                    <div class="text-caption text-capitalize mr-2">
                      <v-chip
                        x-small
                        label
                        outlined
                        :color="alert.alert_type.includes('alarm') ? 'alarmText' : 'cautionText'"
                      >
                        {{ alert.alert_type }}
                      </v-chip>
                    </div>
                    <router-link class="pointer" :to="`/scavenger/${alert.device_id}`"
                      >{{ alert.message || alert.alert_name }}
                    </router-link>
                    <span class="text-caption secondary--text font-weight-bold" v-if="alert.actual_value"
                      >- {{ alert.actual_value | round }}</span
                    >
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">{{ alert.timestampISO | date }} </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-row v-else class="d-flex justify-center mt-3">
              <v-col cols="12" class="text-caption text-center font-weight-bold primaryGrey--text"
                >No logs found!</v-col
              ></v-row
            >
            <div v-if="active_alerts && active_alerts.length > 0" class="mt-6 d-flex justify-center">
              <v-btn x-small class="ma-1 font-weight-bold" color="error" @click="clearActiveAlerts"> Clear Logs </v-btn>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>
    <ConfirmationDialogVT
      v-if="show_delete_dialog"
      :id="dialog_alert_id"
      :buttonText="'Delete'"
      :text="'This alert will be deleted!'"
      @confirm_action="deleteAlert"
      @closeDialog="show_delete_dialog = false"
    />
  </div>
</template>

<script>
import { axios_get, axios_delete, axios_post } from "../services/vt_service";
import { scv } from "../services/powerdata";
import ConfirmationDialogVT from "./ConfirmationDialogVT.vue";

export default {
  name: "AlertsSection",
  components: { ConfirmationDialogVT },
  props: {
    update_alerts: Boolean,
    update_active_alerts: Boolean,
  },
  data: () => ({
    page_config: null,
    network_error: false,
    current_section: null,
    search_text: null,
    alerts: null,
    show_delete_dialog: false,
    dialog_alert_id: null,
    active_alerts: null,
    filter_current_device_alerts: false,
    current_tab: 0,
  }),
  watch: {
    current_tab_selection(newVal) {
      this.current_tab = newVal;
    },
    update_active_alerts(newVal) {
      if (newVal) {
        this.$emit("set-update-active-alerts", false);
        this.getActiveAlerts();
      }
    },
  },
  mounted() {
    this.page_config = this.$config;
    this.getActiveAlerts();
  },
  methods: {
    async getAlerts() {
      this.alerts = await axios_get(scv.get_scv_alerts_data);
    },
    async getActiveAlerts() {
      this.active_alerts = await axios_get(scv.get_scv_active_alerts);
    },
    showAddNewAlertDialog() {
      this.$emit("open-dialog");
    },
    showEditAlertDialog(alert) {
      this.$emit("open-dialog", alert);
    },
    deleteAlertRule(alert_id) {
      this.dialog_alert_id = alert_id;
      this.show_delete_dialog = true;
    },
    async deleteAlert() {
      this.network_error = false;
      await axios_delete(scv.delete_scv_alert, {
        id: this.dialog_alert_id,
      });
      this.show_delete_dialog = false;
      this.error_message = "Alert deleted successfully!";
      this.snackbar = true;
      this.getAlerts();
    },
    async markAllRead() {
      this.network_error = false;
      await axios_post(scv.update_scv_active_alerts);
      this.error_message = "All alerts marked as read!";
      this.snackbar = true;
      this.getActiveAlerts();
    },
    async clearActiveAlerts() {
      this.network_error = false;
      await axios_post(scv.clear_scv_active_alerts);
      this.error_message = "All logs cleared!";
      this.snackbar = true;
      this.getActiveAlerts();
    },
    updateDeviceFilter(filter) {
      this.$emit("update-device-filter", filter);
      this.filter_current_device_alerts = filter;
    },
  },
  computed: {
    filtered_alerts() {
      if (!this.alerts || this.$store.state.update_alerts || this.update_alerts) {
        this.$store.commit("setUpdateAlerts", false);
        this.$emit("set-update-alerts", false);
        this.getAlerts();
      }
      let text = this.search_text
        ? this.search_text.toLowerCase()
        : this.filter_current_device_alerts
          ? this.device_label_obj[this.current_device_id].toLowerCase()
          : null;
      return this.alerts
        ? this.alerts.filter((alert) => {
            let filter = false;
            if (text && alert.device_id)
              filter =
                alert.device_id.toLowerCase().includes(text) ||
                alert.alert_name.toLowerCase().includes(text) ||
                alert.message.toLowerCase().includes(text);
            if (text && alert.tag) {
              let tags = this.$store.state.tags[this.current_device_id] || [];
              filter = filter || tags.indexOf(alert.tag) != -1;
            }
            return text ? filter : true;
          })
        : null;
    },
    unseen_alerts() {
      return this.active_alerts ? this.active_alerts.filter((alert) => !alert.is_seen).length : 0;
    },
    devices: function () {
      return this.$store.state.devices
        ? this.$store.state.devices.map((device) => {
            let label = `${device.TransformerName ? device.TransformerName : device.AssetID ? device.AssetID : device.DeviceID}`;
            return { label, ...device };
          })
        : [];
    },
    device_label_obj: function () {
      return this.devices.reduce((acc, device) => {
        acc[device.DeviceID] =
          `${device.TransformerName ? device.TransformerName : device.AssetID ? device.AssetID : device.DeviceID}`;
        return acc;
      }, {});
    },
    current_device_id: function () {
      let device_id = this.$route.params.deviceId || "";
      this.updateDeviceFilter(!!device_id);
      return device_id;
    },
  },
};
</script>
<style lang="scss" scoped>
.border-bottom {
  border-bottom: 1px solid var(--v-borderLight-base);
}
.v-application .v-navigation-drawer .v-list-item.alert {
  margin: 0 !important;
  padding: 0 !important;
}
.text-wrap {
  white-space: normal;
  text-wrap: auto;
}
</style>
